/**
 * Rybbit Analytics integration.
 *
 * SDK loading: <script> tag in public/index.html:12-17, conditionally
 *              injected when VUE_APP_RYBBIT_ENABLED === 'true'.
 * Global:      window.rybbit
 * Env vars:    VUE_APP_RYBBIT_ENABLED  ('true' to load the script)
 *              VUE_APP_RYBBIT_URL      (script + tracking host)
 *              VUE_APP_RYBBIT_SITE_ID  (Rybbit site identifier)
 *
 * Status: ACTIVE — second analytics pipeline alongside GTM.
 *
 * This module is the CANONICAL home for all Rybbit logic. It owns:
 *   - RYBBIT_CONFIG           event map / skip list / item-list events
 *   - rybbit_is_available     presence check
 *   - rybbit_event            direct API (window.rybbit.event passthrough)
 *   - rybbit_track_event      alias of rybbit_event for naming consistency
 *   - rybbit_event_from_custom normalizer used by custom_event()
 *   - 5 private helpers       (_rybbit_*) supporting the normalizer
 *
 * Naming convention: every public function is prefixed with `rybbit_` so
 * the Rybbit origin is obvious at every call site, even when imported
 * alongside other integration modules.
 *
 * Backward compatibility:
 *   `rybbit_event` is re-exported directly by src/utilities/index.js
 *   (the `@utilities` barrel) so existing imports such as
 *   `import { rybbit_event } from '@utilities'` keep working unchanged.
 *   `commons.js` only imports `rybbit_event_from_custom` for internal use
 *   inside `custom_event()` — it no longer re-exports anything Rybbit-related.
 *
 * Existing call sites (as of 2026-04-13):
 *   - src/utilities/commons.js:custom_event()    rybbit_event_from_custom
 *   - src/utilities/index.js                     re-export of rybbit_event
 *   - src/sites/liveeps/components/blocks/booking-engine.vue:270,762
 *   - src/sites/liveflight/components/views/seat-selection.vue:105,1109
 */

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Rybbit configuration.
 * Exported so callers/tests can inspect the live mapping.
 */
export const RYBBIT_CONFIG = {
  // GA4 event names → Rybbit-normalized names
  eventMap: {
    'confirmedCheckout': 'checkout_completed',
    'failedCheckout': 'checkout_failed',
    'initiateCheckout': 'checkout_started',
    'begin_checkout': 'checkout_started',
    'add_to_cart': 'cart_add',
    'purchase': 'checkout_completed',
    'book': 'booking_completed',
  },
  // Events to drop (noisy or duplicated)
  skipEvents: [
    'confirmedCheckout',  // duplicate: `purchase` carries richer data
    'welfare',            // noisy: fires on every session
    'customizationUse',   // noisy: fires on every session
  ],
  // Events shaped as item lists (use min-price aggregation)
  itemListEvents: ['view_item', 'view_item_list'],
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function rybbit_is_available() {
  return Boolean(window.rybbit)
}

/**
 * Direct passthrough to `window.rybbit.event`.
 *
 * @param {string} event_name - Snake_case event name recommended.
 * @param {Object} [props]    - Event properties.
 */
export function rybbit_event(event_name, props = {}) {
  if (!rybbit_is_available()) return
  if (!event_name || typeof event_name !== 'string') return
  window.rybbit.event(event_name, props)
}

/**
 * Naming-consistent alias of `rybbit_event`. Provided so callers that
 * import the integration module see a uniform `rybbit_*` API.
 */
export function rybbit_track_event(event_name, props) {
  return rybbit_event(event_name, props || {})
}

/**
 * Normalize and forward an event coming from `custom_event(...)`.
 * Handles the conversion from GA4-shaped payloads to Rybbit's flat schema,
 * applies the eventMap, optionally rewrites SSO into session_start, and
 * drops events listed in `skipEvents`.
 *
 * @param {string|Object} action  - Event name OR a complete GA4 event object.
 * @param {string} [label]
 * @param {number} [value]
 * @param {string} [category]
 */
export function rybbit_event_from_custom(action, label, value, category) {
  if (!rybbit_is_available()) return
  if (!_rybbit_validate_action(action)) return

  // 1. Parsing — extract eventName + props depending on the action shape
  let parsed
  if (typeof action === 'object') {
    parsed = _rybbit_parse_ga4_event(action)
  } else {
    parsed = _rybbit_parse_standard_event(action, label, value, category)
  }

  // 2. Mapping — normalize event name
  let event_name = _rybbit_map_event_name(parsed.eventName)
  let props = parsed.props

  // 3. SSO handling — rewrite to session_start when applicable
  if (event_name === 'sso') {
    const sso_result = _rybbit_handle_sso_event(category, props)
    if (!sso_result) return
    event_name = sso_result.eventName
    props = sso_result.props
  }

  // 4. Drop configured noisy / duplicate events
  if (RYBBIT_CONFIG.skipEvents.includes(event_name)) return

  // 5. Send
  rybbit_event(event_name, props)
}

// ---------------------------------------------------------------------------
// Private helpers (rybbit_event_from_custom internals)
// ---------------------------------------------------------------------------

function _rybbit_validate_action(action) {
  if (!action) return false
  if (typeof action === 'object') {
    return action.event && typeof action.event === 'string'
  }
  return true
}

function _rybbit_parse_ga4_event(action) {
  const event_name = action.event
  const props = {}

  if (!action.ecommerce) {
    return { eventName: event_name, props }
  }

  const ecommerce = action.ecommerce

  // Item-list events: aggregate by min price
  if (
    RYBBIT_CONFIG.itemListEvents.includes(event_name) &&
    ecommerce.items &&
    ecommerce.items.length > 0
  ) {
    const prices = ecommerce.items.map(function (item) {
      return parseFloat(item.price) || 0
    })
    props.value = Math.min.apply(null, prices)
    props.items_count = ecommerce.items.length
  } else {
    // Other events: handle net / gross prices
    const price_net = parseFloat(ecommerce.value) || 0
    const price_gross = parseFloat(ecommerce.live_price) || 0
    props.price_gross = price_gross || price_net
    props.price_net = price_net
    if (price_gross && price_net > 0) {
      props.markup = (price_gross - price_net).toFixed(2)
    }
  }

  if (ecommerce.transaction_id) props.transaction_id = ecommerce.transaction_id
  if (ecommerce.currency) props.currency = ecommerce.currency

  return { eventName: event_name, props }
}

function _rybbit_parse_standard_event(action, label, value, category) {
  const props = {}
  if (label) props.label = label
  if (value) props.value = parseFloat(value) || 0
  if (category) props.category = category
  return { eventName: action, props }
}

function _rybbit_map_event_name(event_name) {
  return RYBBIT_CONFIG.eventMap[event_name] || event_name
}

/**
 * Convert an SSO event into a `session_start` event when the URL carries
 * the full UTM pattern (token + utm_source + utm_campaign). Returns null
 * when the event must be dropped.
 */
function _rybbit_handle_sso_event(category, current_props) {
  // SSO fires 3 times in GA4 with different categories; for Rybbit we keep
  // only the `userBehaviour` one to avoid duplication.
  if (category !== 'userBehaviour') return null

  const url_params = new URLSearchParams(window.location.search)
  const token = url_params.get('token')
  const utm_source = url_params.get('utm_source')
  const utm_campaign = url_params.get('utm_campaign')

  if (!token || !utm_source || !utm_campaign) return null

  const props = {
    credit_available: current_props.value || 0,
    utm_source: utm_source,
    utm_campaign: utm_campaign,
  }

  const utm_medium = url_params.get('utm_medium')
  const utm_content = url_params.get('utm_content')
  const utm_term = url_params.get('utm_term')
  if (utm_medium) props.utm_medium = utm_medium
  if (utm_content) props.utm_content = utm_content
  if (utm_term) props.utm_term = utm_term

  return { eventName: 'session_start', props }
}
